/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { Fragment, useContext } from 'react';

import { Link } from '@zapier/common-components';
import { DateTime } from '@zapier/date';
import { Colors } from '@zapier/design-system-revenue/src/constants';
import { Price } from '@zapier/currency';

import { MeteredTiersTable } from '@zapier/design-system-revenue/src/components/other';

import { Interval, PlanTier } from '@zapier/design-system-revenue/src/types';
import { CurrencyContext } from '@zapier/design-system-revenue/src/contexts/CurrencyContext';

type AmountDueOnNextIntervalProps = {
  isPlanMetered: boolean;
  meteredTiers: PlanTier[];
  nextPaymentDate: string;
  planBillingAmount: number;
  planInterval: Interval;
  planNumFreeMeteredTasks: number;
};

const nextIntervalCss = css`
  background-color: ${Colors.storm};
  color: ${Colors.steel};
  padding: 10px;
`;

const lastParagraphCss = css`
  margin: 0;
`;

const tableStyle = css`
  margin-bottom: 15px;
`;

export const AmountDueOnNextInterval = ({
  isPlanMetered,
  meteredTiers,
  nextPaymentDate,
  planBillingAmount,
  planInterval,
  planNumFreeMeteredTasks,
}: AmountDueOnNextIntervalProps) => {
  const selectedCurrency = useContext(CurrencyContext);
  const currencyIsUSD = selectedCurrency.name === 'USD';

  const amount = (
    <strong>
      <Price currency={selectedCurrency}>{planBillingAmount}</Price>
    </strong>
  );

  return (
    <div css={nextIntervalCss}>
      <p>
        You authorize Zapier to charge you for the amount above.{' '}
        {!currencyIsUSD && 'Price shown is valid for 24 hours.'}
      </p>
      <p>Your subscription renews each {planInterval} until you cancel.</p>
      <p>
        The next charge will be {amount}, minus any applicable discounts,
        {nextPaymentDate && (
          <Fragment>
            {' '}
            on{' '}
            <strong>
              <DateTime date={nextPaymentDate} />
            </strong>
            {currencyIsUSD ? '.' : ','}{' '}
          </Fragment>
        )}
        {!currencyIsUSD && (
          <Fragment>
            unless the currency exchange rate changes. The actual price is
            calculated using the exchange rate at the time of payment. Price
            charged excludes all taxes, levies and duties.
          </Fragment>
        )}
      </p>
      {isPlanMetered && (
        <Fragment>
          Your plan includes{' '}
          <strong>{planNumFreeMeteredTasks.toLocaleString()} tasks</strong> per
          month. Extra tasks used will be charged monthly at the below rates.
        </Fragment>
      )}
      {isPlanMetered && (
        <div css={tableStyle}>
          <MeteredTiersTable tiers={meteredTiers} />
        </div>
      )}
      <p css={lastParagraphCss}>
        By clicking the button below, you understand and agree to these terms
        and Zapier’s{' '}
        <Link href="/privacy" target="_blank">
          privacy policy
        </Link>{' '}
        and{' '}
        <Link href="/terms" target="_blank">
          terms of service
        </Link>
        .
      </p>
    </div>
  );
};
